import arrowDownSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-down-small';
import arrowUpSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-up-small';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import React, { useCallback, useMemo, useState } from 'react';
import { PatternSegment, SegmentType, SegmentValidationErrorCode, validateSegment } from '../../utils/PatternEngine';
import { Alert, Button, Input, Select, SelectOption, useTranslation } from '../blocks';

export interface SegmentCardProps {
    segment: PatternSegment;
    index: number;
    onUpdate: (index: number, segment: PatternSegment) => void;
    onRemove: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    isFirst: boolean;
    isLast: boolean;
}

const SEGMENT_TYPE_OPTIONS: SegmentType[] = ['exactText', 'digits', 'letters', 'alphanumeric', 'separator'];

const SEPARATOR_OPTIONS = [
    { value: '-', key: 'separator-dash' },
    { value: '_', key: 'separator-underscore' },
    { value: '.', key: 'separator-period' },
    { value: '#', key: 'separator-hash' },
];

const SEGMENT_TYPE_TRANSLATION_KEYS: Record<SegmentType, string> = {
    exactText: 'segmentType-exactText',
    digits: 'segmentType-digits',
    letters: 'segmentType-letters',
    alphanumeric: 'segmentType-alphanumeric',
    separator: 'segmentType-separator',
};

const ERROR_CODE_TO_TRANSLATION_KEY: Record<SegmentValidationErrorCode, string> = {
    [SegmentValidationErrorCode.TEXT_VALUE_REQUIRED]: 'textValueRequired-fieldError',
    [SegmentValidationErrorCode.TEXT_VALUE_INVALID]: 'textValueInvalid-fieldError',
    [SegmentValidationErrorCode.MIN_LENGTH_INVALID]: 'lengthInvalid-fieldError',
    [SegmentValidationErrorCode.MAX_LENGTH_INVALID]: 'lengthInvalid-fieldError',
    [SegmentValidationErrorCode.MIN_EXCEEDS_MAX]: 'minExceedsMax-fieldError',
    [SegmentValidationErrorCode.INVALID_SEPARATOR]: 'invalidSeparator-fieldError',
    [SegmentValidationErrorCode.UNKNOWN_TYPE]: 'unknownType-fieldError',
};

function getDefaultSegment(type: SegmentType, id: string): PatternSegment {
    switch (type) {
        case 'exactText':
            return { id, type, value: '' };
        case 'digits':
        case 'letters':
        case 'alphanumeric':
            return { id, type, value: '', minLength: 1, maxLength: 1 };
        case 'separator':
            return { id, type, value: '-' };
        default: {
            const exhaustiveCheck: never = type;
            throw new Error(`Unhandled segment type: ${exhaustiveCheck}`);
        }
    }
}

export const SegmentCard: React.FC<SegmentCardProps> = ({
    segment,
    index,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
}) => {
    const { t } = useTranslation('regexPatternModal');

    const [dirty, setDirty] = useState(false);

    const validation = useMemo(() => validateSegment(segment), [segment]);

    const handleTypeChange = useCallback(
        (newType: string) => {
            setDirty(false);
            const updated = getDefaultSegment(newType as SegmentType, segment.id);
            onUpdate(index, updated);
        },
        [index, segment.id, onUpdate]
    );

    const handleValueChange = useCallback(
        (newValue: string) => {
            setDirty(true);
            onUpdate(index, { ...segment, value: newValue });
        },
        [index, segment, onUpdate]
    );

    const handleMinLengthChange = useCallback(
        (newMin: string) => {
            setDirty(true);
            const sanitized = newMin.replace(/[^0-9]/g, '');
            const parsed = sanitized === '' ? undefined : Number(sanitized);
            onUpdate(index, { ...segment, minLength: parsed });
        },
        [index, segment, onUpdate]
    );

    const handleMaxLengthChange = useCallback(
        (newMax: string) => {
            setDirty(true);
            const sanitized = newMax.replace(/[^0-9]/g, '');
            const parsed = sanitized === '' ? undefined : Number(sanitized);
            onUpdate(index, { ...segment, maxLength: parsed });
        },
        [index, segment, onUpdate]
    );

    const handleSeparatorChange = useCallback(
        (newSep: string) => {
            setDirty(true);
            onUpdate(index, { ...segment, value: newSep });
        },
        [index, segment, onUpdate]
    );

    const handleRemove = useCallback(() => {
        onRemove(index);
    }, [index, onRemove]);

    const handleMoveUp = useCallback(() => {
        onMoveUp(index);
    }, [index, onMoveUp]);

    const handleMoveDown = useCallback(() => {
        onMoveDown(index);
    }, [index, onMoveDown]);

    const errorKey =
        dirty && !validation.isValid && validation.errorCode ? ERROR_CODE_TO_TRANSLATION_KEY[validation.errorCode] : '';
    const showError = dirty && !validation.isValid;

    const { errorCode } = validation;
    const showMinError =
        dirty &&
        (errorCode === SegmentValidationErrorCode.MIN_LENGTH_INVALID ||
            errorCode === SegmentValidationErrorCode.MIN_EXCEEDS_MAX);
    const showMaxError =
        dirty &&
        (errorCode === SegmentValidationErrorCode.MAX_LENGTH_INVALID ||
            errorCode === SegmentValidationErrorCode.MIN_EXCEEDS_MAX);

    const renderTypeSpecificFields = () => {
        switch (segment.type) {
            case 'exactText':
                return (
                    <Input
                        value={segment.value}
                        onChange={handleValueChange}
                        label={t('textValue-fieldLabel')}
                        error={showError}
                        data-testid={`segment-${index}-text-value`}
                    />
                );
            case 'digits':
            case 'letters':
            case 'alphanumeric':
                return (
                    <Row widths={['fill', 'fill']}>
                        <Input
                            value={segment.minLength != null ? String(segment.minLength) : ''}
                            onChange={handleMinLengthChange}
                            label={t('minLength-fieldLabel')}
                            type={'number'}
                            error={showMinError}
                            data-testid={`segment-${index}-min-length`}
                        />
                        <Input
                            value={segment.maxLength != null ? String(segment.maxLength) : ''}
                            onChange={handleMaxLengthChange}
                            label={t('maxLength-fieldLabel')}
                            type={'number'}
                            error={showMaxError}
                            data-testid={`segment-${index}-max-length`}
                        />
                    </Row>
                );
            case 'separator':
                return (
                    <Select
                        value={segment.value}
                        onChange={handleSeparatorChange}
                        label={t('separatorCharacter-fieldLabel')}
                        data-testid={`segment-${index}-separator`}
                    >
                        {SEPARATOR_OPTIONS.map((opt) => (
                            <SelectOption key={opt.value} value={opt.value} label={t(opt.key)} />
                        ))}
                    </Select>
                );
            default:
                return null;
        }
    };

    return (
        <Row
            data-testid={`segment-card-${index}`}
            spacing={'300'}
            widths={['fill', 'fit']}
            alignmentVertical={'bottom'}
        >
            <Row spacing={'300'} widths={['fit', 'fill']}>
                <Select
                    value={segment.type}
                    onChange={handleTypeChange}
                    label={t('segmentType-fieldLabel')}
                    size={'small'}
                    data-testid={`segment-${index}-type`}
                >
                    {SEGMENT_TYPE_OPTIONS.map((typeOption) => (
                        <SelectOption
                            key={typeOption}
                            value={typeOption}
                            label={t(SEGMENT_TYPE_TRANSLATION_KEYS[typeOption])}
                        />
                    ))}
                </Select>
                <Column spacing={'xsmall'}>
                    {renderTypeSpecificFields()}
                    {!validation.isValid && errorKey && (
                        <Alert type={'error'} size={'small'} data-testid={`segment-${index}-error`}>
                            {t(errorKey)}
                        </Alert>
                    )}
                </Column>
            </Row>
            <Row spacing={'100'} widths={'fit'} alignmentVertical={'bottom'}>
                <Button
                    type={'icon'}
                    size={'small'}
                    onClick={handleMoveUp}
                    disabled={isFirst}
                    label={t('moveUp-buttonLabel')}
                    data-testid={`segment-${index}-move-up`}
                >
                    <Icon tokens={arrowUpSmallTokens} />
                </Button>
                <Button
                    type={'icon'}
                    size={'small'}
                    onClick={handleMoveDown}
                    disabled={isLast}
                    label={t('moveDown-buttonLabel')}
                    data-testid={`segment-${index}-move-down`}
                >
                    <Icon tokens={arrowDownSmallTokens} />
                </Button>
                <Button
                    type={'icon'}
                    size={'small'}
                    onClick={handleRemove}
                    label={t('removeSegment-buttonLabel')}
                    data-testid={`segment-${index}-remove`}
                >
                    <Icon tokens={closeSmallTokens} />
                </Button>
            </Row>
        </Row>
    );
};
